import { useState } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { Row } from "../../components/Row";
import { Ruling } from "../../components/Ruling";
import { MaterialSelection } from "../../types/material";
import { RootStore } from "../../state/store";
import { shuffle } from "../../util/generalUtil";

interface Player {
  name: string;
  material: MaterialSelection;
  lives: number;
  verseStatus: number; // 0 if incorrect or incomplete
}
interface Verse {
  book: number;
  chapter: number;
  verse: number;
  time: number;
}

export function EpicBattle() {
  const location = useLocation();
  const material = useSelector((state: RootStore) => state.material.material);
  const settings = useSelector((state: RootStore) => state.settings);
  const [players, setPlayers] = useState<Player[]>(
    shuffle(
      (location.state?.epicBattle?.players || []).map(
        (player: { name: string; material: MaterialSelection }) => ({
          name: player.name,
          material: player.material,
          lives: 3,
          verseStatus: 0,
        })
      )
    )
  );
  const [turn, setTurn] = useState(-1);
  const [currentVerse, setCurrentVerse] = useState<Verse>({
    book: 0,
    chapter: 0,
    verse: 0,
    time: 0, // time started, for verse in progress, total time in seconds for finished verse
  });
  const [phase, setPhase] = useState<
    "START" | "BETWEEN" | "READY" | "ANSWERING" | "RULING" | "FINISHED"
  >("START");

  if (players.length === 0) return <Navigate to="/" />;

  return (
    <div className="page">
      <Row>
        <Link className="back-button" to="/">
          Go Back
        </Link>
        <h1 style={{ marginLeft: 10 }}>Epic Battle</h1>
      </Row>
      <div style={{ display: "flex", marginTop: 10 }}>
        <div style={{ width: 200 }}>Name</div>
        <div style={{ width: 50, marginLeft: 10 }}>Lives</div>
        <div style={{ marginLeft: 10 }}>Verse Status</div>
      </div>
      {players.map((player, index) => (
        <div
          key={player.name}
          style={{
            display: "flex",
            marginTop: 4,
            textDecoration: player.lives === 0 ? "line-through" : undefined,
          }}
        >
          <div
            style={{
              width: 200,
              backgroundColor:
                turn === index
                  ? settings.lightMode
                    ? "lightblue"
                    : "darkblue"
                  : undefined,
            }}
          >
            {player.name}
          </div>
          <div style={{ width: 50, marginLeft: 10 }}>{player.lives}</div>
          <div style={{ marginLeft: 10 }}>
            {turn > index && player.lives > 0
              ? player.verseStatus === 0
                ? "Incorrect"
                : `${player.verseStatus} Seconds`
              : "-"}
          </div>
        </div>
      ))}
      {phase !== "FINISHED" && (
        <div style={{ marginTop: 30 }}>
          {(phase === "BETWEEN" || phase === "START") && (
            <button
              className="clickable"
              key="start-round"
              style={{ marginTop: 10 }}
              onClick={() => {
                const newPlayers = [...players];
                if (phase === "BETWEEN") {
                  // reduce lives, if at least one player got the verse right
                  if (
                    newPlayers.some((p) => p.lives > 0 && p.verseStatus > 0)
                  ) {
                    let someWrong = false;
                    for (let i = 0; i < newPlayers.length; i++) {
                      if (
                        newPlayers[i].lives > 0 &&
                        newPlayers[i].verseStatus === 0
                      ) {
                        newPlayers[i] = {
                          ...newPlayers[i],
                          lives: newPlayers[i].lives - 1,
                        };
                        someWrong = true;
                      }
                    }
                    if (!someWrong) {
                      // most time loses a life
                      let maxIndex = -1;
                      let maxTime = 0;
                      newPlayers.forEach((p, i) => {
                        if (p.verseStatus > maxTime) {
                          maxIndex = i;
                          maxTime = p.verseStatus;
                        }
                      });
                      if (maxIndex >= 0)
                        newPlayers[maxIndex] = {
                          ...newPlayers[maxIndex],
                          lives: newPlayers[maxIndex].lives - 1,
                        };
                    }
                  }
                  for (let i = 0; i < newPlayers.length; i++) {
                    newPlayers[i] = {
                      ...newPlayers[i],
                      verseStatus: 0,
                    };
                  }
                  setPlayers(newPlayers);
                }

                // Start round, or finish game
                const playersAlive = newPlayers.filter((p) => p.lives > 0);
                if (playersAlive.length <= 1) {
                  setPhase("FINISHED");
                  setTurn(-1);
                } else {
                  setTurn(newPlayers.findIndex((p) => p.lives > 0));
                  setPhase("READY");
                }
              }}
            >
              {`${phase === "START" ? "Start" : "Finish"} Round`}
            </button>
          )}
          {phase === "READY" && (
            <button
              className="clickable"
              key="next-verse"
              style={{ marginTop: 20 }}
              onClick={() => {
                setPhase("ANSWERING");

                // Pick a random verse from the player's material
                const allVerses: Verse[] = [];
                material.forEach((book, index) => {
                  book.chapters.forEach((chapter, chapterIndex) => {
                    const chapters =
                      players[turn].material || settings.selectedChapters;
                    if (
                      chapters &&
                      chapters[index] &&
                      chapters[index][chapterIndex]
                    ) {
                      chapter.forEach((_, verseIndex) => {
                        const chapterFromSettings =
                          chapters[index][chapterIndex];
                        const chapterSettings =
                          typeof chapterFromSettings === "boolean"
                            ? Array(chapter.length).fill(chapterFromSettings)
                            : chapterFromSettings;
                        if (chapterSettings[verseIndex]) {
                          allVerses.push({
                            book: index,
                            chapter: chapterIndex,
                            verse: verseIndex,
                            time: 0,
                          });
                        }
                      });
                    }
                  });
                });
                if (settings.vcRandomize) shuffle(allVerses);

                // Set the verse, with the current time
                setCurrentVerse({ ...allVerses[0], time: Date.now() });
              }}
            >
              Next Verse
            </button>
          )}
          {phase === "ANSWERING" && (
            <>
              <p style={{ fontSize: 16 }}>
                {`${material[currentVerse.book].bookName} ${
                  currentVerse.chapter + 1
                }:${currentVerse.verse + 1}`}
              </p>
              <button
                className="clickable"
                onClick={() => {
                  // Start ruling, and determine time
                  setPhase("RULING");
                  setCurrentVerse({
                    ...currentVerse,
                    time:
                      Math.round((Date.now() - currentVerse.time) / 10) / 100,
                  });
                }}
              >
                Show Answer
              </button>
            </>
          )}
          {phase === "RULING" && (
            <>
              <p style={{ fontSize: 16 }}>
                {`${material[currentVerse.book].bookName} ${
                  currentVerse.chapter + 1
                }:${currentVerse.verse + 1}`}
              </p>
              <p>
                {
                  material[currentVerse.book].chapters[currentVerse.chapter][
                    currentVerse.verse
                  ].verse
                }
              </p>
              <p>{`${currentVerse.time} Seconds`}</p>
              <Ruling
                onClick={(type) => {
                  if (type === 2) {
                    setPhase("READY");
                    return;
                  }
                  // Assign verse status
                  const newPlayers = [...players];
                  newPlayers[turn] = {
                    ...newPlayers[turn],
                    verseStatus: type === 0 ? currentVerse.time : 0,
                  };
                  setPlayers(newPlayers);

                  // Determine next turn
                  let newTurn = turn;
                  for (let i = newTurn + 1; i < players.length; i++) {
                    if (newPlayers[i].lives > 0) {
                      newTurn = i;
                      break;
                    }
                  }
                  if (newTurn === turn) {
                    // End of round
                    setTurn(players.length);
                    setPhase("BETWEEN");
                  } else {
                    // Next player
                    setPhase("READY");
                    setTurn(newTurn);
                  }
                }}
              />
            </>
          )}
        </div>
      )}
      {phase === "FINISHED" && (
        <p style={{ fontSize: 18, marginTop: 40 }}>Game Over!</p>
      )}
    </div>
  );
}
